<script setup>

import {computed, onMounted, ref} from "vue";

const hoursPerWeekOnManualTasks = ref(20);
const hourlyCost = ref(50);
const numberOfTeamMembers = ref(1);
const expectedTimeReduction = ref(50);

const transitioned = ref(false)
const calculating = ref(false);
const seeResult = ref(false);

const results = computed(() => {
    // Hours saved per week
    const hoursSavedPerWeek = hoursPerWeekOnManualTasks.value * (expectedTimeReduction.value / 100);

    // Hours saved per month (assuming 4.33 weeks per month)
    const hoursSavedPerMonth = hoursSavedPerWeek * 4.33;

    // Cost savings per month
    const costSavingsPerMonth = hoursSavedPerMonth * hourlyCost.value * numberOfTeamMembers.value;

    // Cost savings per year
    const costSavingsPerYear = costSavingsPerMonth * 12;

    // Equivalent FTE savings (assuming 40 hours per week, 4.33 weeks per month = 173.2 hours per month)
    const hoursPerMonthPerFTE = 40 * 4.33;
    const equivalentFTESavings = hoursSavedPerMonth / hoursPerMonthPerFTE;

    // ROI from automation (simplified: assuming automation cost is 10% of annual savings)
    // ROI = ((Savings - Cost) / Cost) * 100
    const estimatedAutomationCost = costSavingsPerYear * 0.1;
    const roiFromAutomation = estimatedAutomationCost > 0 ? ((costSavingsPerYear - estimatedAutomationCost) / estimatedAutomationCost) * 100 : 0;

    return {
        hoursSavedPerWeek: hoursSavedPerWeek.toLocaleString('en-US', {maximumFractionDigits:1}).replaceAll(',', ' '),
        hoursSavedPerWeekRaw: hoursSavedPerWeek,
        hoursSavedPerMonth: hoursSavedPerMonth.toLocaleString('en-US', {maximumFractionDigits:1}).replaceAll(',', ' '),
        hoursSavedPerMonthRaw: hoursSavedPerMonth,
        costSavingsPerMonth: costSavingsPerMonth.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        costSavingsPerMonthRaw: costSavingsPerMonth,
        costSavingsPerYear: costSavingsPerYear.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        costSavingsPerYearRaw: costSavingsPerYear,
        equivalentFTESavings: equivalentFTESavings.toLocaleString('en-US', {maximumFractionDigits:2}).replaceAll(',', ' '),
        equivalentFTESavingsRaw: equivalentFTESavings,
        roiFromAutomation: roiFromAutomation.toFixed(1),
    };
})

const calculateInitial = () => {
    calculating.value = true

    setTimeout(function(){
        seeResult.value = true;

        setTimeout(function() {
            window.scrollBy({
                top: 500,
                behavior: 'smooth'
            });
        }, 300);
    }, 300)
}

onMounted(() => {
    setTimeout(() => {
        transitioned.value = true;
    }, 100);
})

</script>

<template>
    <div>
        <card-basic
            class="transition-all duration-500"
            :class="transitioned ? 'translate-y-0 scale-100' : '-translate-y-4 opacity-0 scale-95'"
        >
            <div class="grid-cols-2 gap-8 md:grid">
                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Hours per week on manual tasks (prospecting, emailing, quoting, invoicing)
                    </p>
                    <input-number
                        max="168"
                        min="0"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="hoursPerWeekOnManualTasks"
                        :show-step-ui="true"
                        placeholder="Hours per week"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Hourly cost (salary + overhead)
                    </p>
                    <input-number
                        max="1000"
                        min="0"
                        :step="5"
                        class="mx-auto w-36"
                        v-model:content="hourlyCost"
                        :show-step-ui="true"
                        placeholder="Hourly cost"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Number of team members
                    </p>
                    <input-number
                        max="1000"
                        min="1"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="numberOfTeamMembers"
                        :show-step-ui="true"
                        placeholder="Team members"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Expected time reduction per task (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="expectedTimeReduction"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="expectedTimeReduction = $event"
                        />{{ expectedTimeReduction }} %
                    </div>
                </div>

                <div
                    v-if="! seeResult"
                    class="flex justify-center col-span-2"
                >
                    <button-primary
                        class="justify-center w-48 mx-auto transition-all"
                        :class="calculating ? 'translate-y-8 opacity-0' : ''"
                        @click="calculateInitial"
                    >
                        Calculate
                    </button-primary>
                </div>

                <div
                    v-if="calculating"
                    class="col-span-2 -mx-4 -mt-6 -mb-4 transition-colors duration-1000 text-wlPrimaryContrast md:-mx-6 md:-mb-6 lg:-mx-8 lg:-mb-8"
                    :class="seeResult ? 'bg-wlPrimary' : 'bg-transparent'"
                >
                    <div class="w-full h-12 transform -translate-y-px bg-white rounded-b-wl"></div>
                    <div
                        class="col-span-2 p-4 pt-3 transition-all duration-1000 rounded-b-wl md:p-6 md:pt-4 lg:p-8 lg:pt-5"
                        :class="seeResult ? 'translate-y-0 opacity-100 max-h-screen' : '-translate-y-8 opacity-0 max-h-0'"
                    >
                        <div class="flex mb-8 space-x-4">
                            <div>
                                <i class="mt-1 text-2xl fa-regular fa-chart-line"></i>
                            </div>
                            <div>
                                <p>By automating manual tasks, you would save <strong>{{ results.hoursSavedPerWeek }} hours per week</strong>.</p>

                                <p>This translates to <strong>{{ results.hoursSavedPerMonth }} hours saved per month</strong>.</p>

                                <p>Cost savings per month: <strong>${{ results.costSavingsPerMonth }}</strong></p>

                                <p>Cost savings per year: <strong>${{ results.costSavingsPerYear }}</strong></p>

                                <p>Equivalent FTE savings: <strong>{{ results.equivalentFTESavings }} FTE</strong></p>

                                <p class="mt-4">
                                    <strong>ROI from automation: {{ results.roiFromAutomation }}%</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </card-basic>
    </div>
</template>

<style scoped>

</style>

